import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Bell, BellOff } from "lucide-react";
import { oneSignalService } from "@/lib/onesignal";
import { isNotificationsAvailable, waitForNotificationsSDK, detectNativeApp } from "@/lib/platform";
import { NotificationPromptDialog } from "@/components/NotificationPromptDialog";
import { useTranslation } from "react-i18next";

export const NotificationSettings = () => {
  const { t } = useTranslation();
  const [isEnabled, setIsEnabled] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [isLoadingPremium, setIsLoadingPremium] = useState(true);
  const [isSupported, setIsSupported] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPromptDialog, setShowPromptDialog] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    checkPremiumStatus();
    checkNotificationSupport();
    checkCurrentSubscription();
  }, []);

  const checkPremiumStatus = async () => {
    setIsLoadingPremium(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setIsLoadingPremium(false);
      return;
    }

    const { data: profile } = await supabase
      .from("profiles")
      .select("is_premium")
      .eq("id", user.id)
      .single();

    setIsPremium(profile?.is_premium || false);
    setIsLoadingPremium(false);
  };

  const checkNotificationSupport = async () => {
    // Check if we're in a native app first
    const detection = await detectNativeApp();

    if (!detection.isNative) {
      setIsSupported(false);
      console.log('[Notifications] Not in native app');
      return;
    }

    // Wait for the SDK to load
    const sdkLoaded = await waitForNotificationsSDK(3000); // Wait up to 3 seconds

    if (sdkLoaded) {
      setIsSupported(true);
      console.log('[Notifications] SDK loaded and ready');
    } else {
      setIsSupported(false);
      console.log('[Notifications] SDK failed to load');
    }
  };

  const checkCurrentSubscription = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase
      .from("profiles")
      .select("onesignal_player_id, goal_reminder_notifications_enabled")
      .eq("id", user.id)
      .maybeSingle();

    // User has goal reminder notifications enabled if they have a player ID AND the setting is enabled
    setIsEnabled(!!profile?.onesignal_player_id && (profile?.goal_reminder_notifications_enabled !== false));
  };

  const subscribeToNotifications = async () => {
    if (!isSupported) {
      toast({
        title: t('settings.notifications.toast_not_supported_title'),
        description: t('settings.notifications.toast_not_supported_desc'),
        variant: "destructive",
      });
      return;
    }

    // Show the prompt dialog which handles all the permission flow
    setShowPromptDialog(true);
  };

  const handlePromptSuccess = async () => {
    // After successful permission grant, enable goal reminders in the database
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("Not authenticated");
      }

      // Enable goal reminder notifications
      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          goal_reminder_notifications_enabled: true
        } as any)
        .eq("id", user.id);

      if (updateError) {
        throw new Error(`Failed to enable goal reminders: ${updateError.message}`);
      }

      setIsEnabled(true);
      toast({
        title: t('settings.notifications.toast_enabled_title'),
        description: t('settings.notifications.toast_enabled_desc'),
      });
    } catch (error) {
      console.error("[NotificationSettings] Error enabling goal reminders:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to enable goal reminders.";
      toast({
        title: t('settings.notifications.toast_error_title'),
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromNotifications = async () => {
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Only disable goal reminder notifications, keep onesignal_player_id intact
      // This allows follow request and penalty notifications to continue working
      await supabase
        .from("profiles")
        .update({ goal_reminder_notifications_enabled: false } as any)
        .eq("id", user.id);

      setIsEnabled(false);
      toast({
        title: t('settings.notifications.toast_disabled_title'),
        description: t('settings.notifications.toast_disabled_desc'),
      });
    } catch (error) {
      console.error("[OneSignal] Unsubscribe error:", error);
      toast({
        title: t('settings.notifications.toast_error_title'),
        description: t('settings.notifications.toast_disable_error'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingPremium) {
    return (
      <div className="flex items-start gap-4 pt-4">
        <div className="p-3 rounded-lg bg-primary/10">
          <Bell className="h-6 w-6 text-primary animate-pulse" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold mb-2">{t('settings.notifications.goal_reminders')}</h3>
          <div className="h-4 bg-muted rounded animate-pulse w-3/4 mb-2"></div>
          <div className="h-4 bg-muted rounded animate-pulse w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="flex items-start gap-4 pt-4">
        <div className="p-3 rounded-lg bg-primary/10">
          <BellOff className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold mb-2">{t('settings.notifications.goal_reminders')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('settings.notifications.upgrade_desc')}
          </p>
          <Button variant="outline" asChild>
            <a href="/upgrade">{t('settings.profile.upgrade')}</a>
          </Button>
        </div>
      </div>
    );
  }

  if (!isSupported) {
    return (
      <div className="flex items-start gap-4 pt-4">
        <div className="p-3 rounded-lg bg-destructive/10">
          <BellOff className="h-6 w-6 text-destructive" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold mb-2">{t('settings.notifications.not_supported')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('settings.notifications.not_supported_desc')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-start gap-4 pt-4">
        <div className="p-3 rounded-lg bg-primary/10">
          <Bell className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold">{t('settings.notifications.goal_reminders')}</h3>
            <Switch
              checked={isEnabled}
              onCheckedChange={(checked) => {
                if (checked) {
                  subscribeToNotifications();
                } else {
                  unsubscribeFromNotifications();
                }
              }}
              disabled={loading}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {isEnabled
              ? t('settings.notifications.reminders_enabled_desc')
              : t('settings.notifications.reminders_disabled_desc')}
          </p>
        </div>
      </div>

      {/* Notification Permission Prompt Dialog */}
      <NotificationPromptDialog
        open={showPromptDialog}
        onOpenChange={setShowPromptDialog}
        onSuccess={handlePromptSuccess}
        onError={(error) => {
          // Only show error if it's not a permission denial
          if (!error.includes("denied")) {
            toast({
              title: "Error",
              description: error,
              variant: "destructive",
            });
          }
        }}
      />
    </>
  );
};
